





















//  Libraries
import Vue from 'vue'
import { mapGetters } from 'vuex'
import mixin from '@/mixins/notifications-mixin'
import moment from 'moment'

//  Types
import Notification from '@/types/Notification.interface'
import { NotificationParams } from '@/types/PaginationParams.interface'
import { NotificationFilterParams } from '@/types/FilterParams.interface'
import Button from '@/types/components/Button.interface'
import IList from '@/types/components/IList.interface'
import ScrollPosition from '@/types/components/ScrollPosition.interface'
import IListTile from '@/types/components/IListTile.interface'
import IListAction, { IListActionType } from '@/types/components/IListAction.interface'
import IListIcon from '@/types/components/IListIcon.interface'

//  Components
import FilterButton from '@/components/FilterButton.vue'
import NotificationProfile from '@/components/NotificationProfile.vue'

export default Vue.extend({
    components: { FilterButton, NotificationProfile },
    mixins: [mixin],
    data() {
        return {
            //  The notifications list filter button
            filter: {
                selected: {
                    display: 'All',
                    key: 'type',
                    value: 'all'
                },
                options: [
                    {
                        display: 'All',
                        key: 'type',
                        value: 'all'
                    },
                    {
                        display: 'Messages',
                        key: 'type',
                        value: 'message'
                    },
                    {
                        display: 'Notifications',
                        key: 'type',
                        value: 'general'
                    },
                    {
                        display: 'Mentions',
                        key: 'type',
                        value: 'mention'
                    }
                ]
            },
            //  Determines if we are ready to use the component
            mounted: false,
            //  Placeholder for our lists
            notificationsPlaceholder: {
                img: 'noNotifications',
                content: 'No Notifications to Display'
            },
            toolbar: {
                title: 'Notifications',
                left: [
                    {
                        title: 'Back',
                        icon: 'mdi-chevron-left',
                        back: '/settings',
                        tooltip: 'Back',
                        permission: 0
                    }
                ],
                right: []
            }
        }
    },
    beforeDestroy() {
        //  Unset any filters and return to the original state
        this.$store.commit('notificationsParamsSet', { page: 1, type: 'all' })

        //  Re-paginate
        this.$store.dispatch('notificationsPaginate')
    },
    async mounted() {
        //  Grab our settings query
        const settings = this.$route.query.settings

        //  If settings is set then open our notification settings popup
        if (settings) this.popNotificationSettings()

        //  Grab our id
        const id = this.$route.query.id

        //  If there is an id attached, find the selected notification
        if (id) this.$store.dispatch('notificationsSelectedNotificationGet', id)

        //  Check if we have a type query param
        const type = this.$route.query.type

        //  If we do, then lets change to that type
        if (type) {
            //  Find our filter matching this type
            const filter = this.filter.options.find((x) => x.value === type)

            //  Set our filter selected
            this.filter.selected = filter

            //  Change our filter
            this.onFilterSelected(filter)
        }

        //  We finished everything so we are now mounted
        this.mounted = true
    },
    computed: {
        ...mapGetters([
            'notifications',
            'notificationsPaginating',
            'notificationsLastPage',
            'scrollPositionLeft'
        ]),
        //  The notifications IList (used for multi-select)
        list(): IList {
            return this.$store.getters.iList('notificationsList')
        },
        //  The pagination params currently loaded
        notificationsParams: {
            get() {
                return this.$store.getters.notificationsParams
            },
            set(val: NotificationParams) {
                this.$store.commit('notificationsParamsSet', val)
            }
        },
        //  The currrently selected notification
        selectedNotification: {
            get(): Notification {
                return this.$store.getters.notificationsSelectedNotification
            },
            set(val: Notification): void {
                this.$store.commit('notificationsSelectedNotificationSet', val)
            }
        },
        //  The buttons displayed in the upper right
        tools(): Button[] {
            //  Count unread notifications
            const unread = this.$store.getters.notificationsUnreadCount

            return [
                {
                    text: 'Mark Selected Read',
                    color: this.$colors.mdPurple,
                    dark: true,
                    hidden: !this.list || this.list.multiSelected.length === 0,
                    icon: 'bi-bookmark-check',
                    outlined: true,
                    small: true,
                    callback: () => this.dismissMultiple()
                },
                {
                    text: 'Mark Selected Unread',
                    color: this.$colors.mdPurple,
                    dark: true,
                    hidden: !this.list || this.list.multiSelected.length === 0,
                    icon: 'bi-bookmark-dash',
                    outlined: true,
                    small: true,
                    callback: () => this.resetMultiple()
                },
                {
                    text: 'Delete Selected',
                    color: this.$colors.mdPurple,
                    dark: true,
                    hidden: !this.list || this.list.multiSelected.length === 0,
                    icon: 'bi-trash',
                    outlined: true,
                    small: true,
                    callback: () => this.deleteMultiple()
                },
                {
                    text: 'Mark All As Read',
                    color: this.$colors.mdPurple,
                    dark: true,
                    disabled: unread === 0,
                    icon: this.$vuetify.breakpoint.xsOnly ? 'bi-card-checklist' : '',
                    hidden: unread === 0 || this.list?.multiSelected.length > 0,
                    outlined: true,
                    small: true,
                    callback: () => this.dismissAll()
                },
                {
                    text: 'Notification Settings',
                    color: this.$colors.mdPurple,
                    dark: true,
                    icon: this.$vuetify.breakpoint.xsOnly ? 'bi-gear-wide-connected' : '',
                    hidden: this.list?.multiSelected.length > 0,
                    small: true,
                    callback: () => this.popNotificationSettings()
                }
            ]
        }
    },
    methods: {
        //  Soft delete multiple notifications (used with multi-select)
        deleteMultiple(): void {
            //  Grab our multi-selected notifications
            const notifications = this.list.multiSelected

            //  Get the length of the multiselected list
            const l = notifications.length

            //  If the length is zero, do nothing
            if (l === 0) return

            //  Else, pop a confirm dialog
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Confirm Delete',
                text: `Are you sure you want to delete (${l}) notifications?`,
                icon: 'mdi-alert-circle',
                callback: async (result: boolean): Promise<void> => {
                    //  If the user answered 'NO', do nothing
                    if (!result) return

                    //  Else, create an array of ids that will be deleted
                    const ids = notifications.map((x) => x.id)

                    //  Deselect everything
                    this.$refs.notificationsList.multiSelectReset()

                    //  Remove notification from our list (for instance ui response)
                    this.$store.commit('notificationsRemove', ids)

                    //  Get the selected notification
                    const n = this.selectedNotification

                    //  Check if the selected notification was deleted
                    const isDeleted = n && ids.includes(n.id)

                    //  If the selected notification was deleted, remove it
                    if (isDeleted) this.selectedNotification = null

                    //  Send our changes to the server
                    await this.$store.dispatch('notificationsDelete', ids)

                    //  If all notifications were deleted...
                    if (this.notifications.length === 0) {
                        //  Repaginate the list (we use onFilterSelected to preserve the currently selected filter)
                        this.onFilterSelected(this.filter.selected, true)
                    }
                }
            })
        },
        //  Soft delete a single notification
        deleteNotification(notification: Notification): void {
            //  Pop a confirm dialog
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Confirm Delete',
                text: 'Are you sure you want to delete this notification?',
                icon: 'mdi-alert-circle',
                callback: (result: boolean): void => {
                    //  If the dialog result is false (ie NO), do nothing
                    if (!result) return

                    //  Else, assemble our request payload
                    const ids = [notification.id]

                    //  Send the request to the API
                    this.$store.dispatch('notificationsDelete', ids)

                    //  Remove notification from our list (for instance ui response)
                    this.$store.commit('notificationsRemove', ids)

                    //  Get the selected notification
                    const n = this.selectedNotification

                    //  Check if the selected notification was deleted
                    const isDeleted = n && n.id === notification.id

                    //  If the selected Notification was deleted, set null
                    if (isDeleted) this.selectedNotification = null
                }
            })
        },
        //  Dismiss all unread notifications
        dismissAll(): void {
            //  Get confirmation from the user before dismissing all
            this.$store.dispatch('dialogOpen', {
                type: 'CONFIRM',
                title: 'Mark All As Read?',
                text: 'Are you sure you want to mark all your notifications as read?',
                icon: 'mdi-alert-circle',
                callback: (result: boolean) => {
                    //  If the user answered 'NO' in the dialog, do nothing
                    if (!result) return

                    //  Else, dismiss the notifications (for instant ui response)
                    for (const n of this.notifications) n.read_at = true

                    //  Send the request to the API
                    this.$store.dispatch('notificationsDismissAll')

                    //  Deselect everything
                    this.$refs.notificationsList.multiSelectReset()
                }
            })
        },
        //  Dismiss multiple notifications (used with multi-select)
        dismissMultiple(): void {
            //  Grab our multi-selected notifications
            const notifications = this.list.multiSelected

            //  Get only unread notifications (sometimes people select notifications that have already been read)
            const unread = notifications.filter((x) => !x.read_at)

            //  If unread notifications do NOT exist, no need to send an API request, just reset multiselect
            if (unread.length === 0)
                return this.$refs.notificationsList.multiSelectReset()

            //  Else, update the notifications (for instant UI responsiveness)
            for (const n of unread) n.read_at = true

            //  Create and array of ids that will be dismisses
            const ids = unread.map((x) => x.id)

            //  Send our payload
            this.$store.dispatch('notificationsDismiss', ids)

            //  Deselect everything
            this.$refs.notificationsList.multiSelectReset()
        },
        //  Dismiss a single notification
        dismissNotification(notification: Notification): void {
            //  Check if the notification is unread
            const unread = !notification.read_at

            //  If the notification is unread, dismiss it
            if (unread) {
                //  Set the notification read (for instant UI responsiveness)
                notification.read_at = new Date().toUTCString()

                //  Send changes to the server
                this.$store.dispatch('notificationsDismiss', [notification.id])
            }
        },
        //  Define the buttons shown on the right side of the IList tile
        notificationActions(notification: Notification): IListAction {
            //  Create our IListAction
            const listAction: IListAction = {
                type: IListActionType.menu,
                menu: [
                    {
                        text: 'Mark as Read',
                        icon: 'bi-bookmark-check',
                        color: this.$colors.mdPurple,
                        hidden: notification.read_at != null,
                        callback: () => this.dismissNotification(notification)
                    },
                    {
                        text: 'Mark Unread',
                        icon: 'bi-bookmark-dash',
                        color: this.$colors.mdPurple,
                        hidden: notification.read_at == null,
                        callback: () => this.resetNotification(notification)
                    },
                    {
                        text: 'Delete',
                        icon: 'bi-trash',
                        color: this.$colors.mdPurple,
                        callback: () => this.deleteNotification(notification)
                    }
                ]
            }

            //  Return our action
            return listAction
        },
        //  Define the notification IList tile
        notificationsTile(): IListTile {
            return {
                actionContent: (n: Notification): IListAction =>
                    this.notificationActions(n),
                leftIcon: (n: Notification): IListIcon => this.notificationIcon(n),
                line1: (n: Notification): string => n.data.title,
                line2: (n: Notification): string => n.data.text,
                callback: (n: Notification) => this.onNotificationSelected(n)
            }
        },
        //  Handle the filter selected event (override = true will force pagination)
        onFilterSelected(filter: NotificationFilterParams, override: boolean): void {
            //  Grab the key and value from our filter
            const key = filter.key
            const value = filter.value

            //  Get our current params
            const params = this.notificationsParams

            //  Check if we currently have this filter applied
            const isSame = params[key] === value

            //  If the filter is the same (AND NOT override), don't do anything
            if (isSame && !override) return

            //  Else, apply this to our params
            params[key] = value

            //  Also set page to 1 since we changed filter
            params.page = 1

            //  Update pagination params (our computed setter will apply this in the store)
            this.notificationsParams = params

            //  Deselect multiselected items
            this.$refs.notificationsList.multiSelectReset()

            //  Deselect selected notification
            this.selectedNotification = null

            //  The filter was changed so we should paginate the new filter
            this.$store.dispatch('notificationsPaginate')
        },
        //  Handle the notification selected event
        onNotificationSelected(notification: Notification): void {
            //  Get our current selected notification
            const selected = this.selectedNotification

            //  Check if the notification is different from the currently selected notification
            const isNew = !selected || selected.id != notification.id

            //  If it is NOT new then do nothing
            if (!isNew) return

            //  Check if the notification is unread
            const unread = !notification.read_at

            //  If the notification is unread, dismiss it (since the user is viewing the notification we automatically dismiss it)
            if (unread) this.dismissNotification(notification)

            //  Set the selected notification
            this.selectedNotification = notification
        },
        //  Pop the user notifications settings dialog
        popNotificationSettings(): void {
            this.$store.dispatch('dialogOpen', {
                type: 'APP',
                component: 'notifications-settings',
                width: '775px',
                closeable: true,
                closeOnSubmit: true
            })
        },
        //  Paginate notification using the current pagination params
        paginateNotifications(): void {
            //  Get current pagination params
            const params = this.notificationsParams

            //  Determine the next page
            const nextPage = params.page + 1

            //  Determine is the page exists, or we're already at the end of pagination
            const shouldPaginate = nextPage <= this.notificationsLastPage

            //  If we should NOT paginate, do nothing
            if (!shouldPaginate) return

            //  Else, update pagination params
            params.page = nextPage
            this.$store.commit('notificationsParamsSet', params)

            //  Paginate the next page
            this.$store.dispatch('notificationsPaginate')
        },
        //  Mark a single notification as 'Unread' (ie reset the notification)
        resetNotification(notification: Notification) {
            //  Set the notification to unread (for instant UI responsiveness)
            notification.read_at = null

            //  Send changes to the server
            this.$store.dispatch('notificationsReset', [notification.id])
        },
        //  Mark selected notifications as 'Unread' (ie reset the notifications)
        resetMultiple() {
            //  Grab our multi-selected notifications
            const notifications = this.list.multiSelected

            //  Get only read notifications (sometimes people select notifications that are already unread)
            const readAt = notifications.filter((x) => x.read_at)

            //  If readAt notifications do NOT exist, no need to send an API request, just reset multiselect
            if (readAt.length === 0)
                return this.$refs.notificationsList.multiSelectReset()

            //  Else, reset the notifications
            for (const n of readAt) n.read_at = null

            //  Commit changes to store (for instant UI responsiveness)
            this.$store.commit('notificationsPush', readAt)

            //  Create and array of ids that will be dismisses
            const ids = readAt.map((x) => x.id)

            //  Send our payload
            this.$store.dispatch('notificationsReset', ids)

            //  Deselect everything
            this.$refs.notificationsList.multiSelectReset()
        }
    },
    watch: {
        scrollPositionLeft(position: ScrollPosition): void {
            if (!this.notificationsPaginating && position.bottom)
                this.paginateNotifications()
        }
    }
})
